import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { apiRequest } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import '../admin/quotes/quote-generator.css';
import '../admin/settings/settings.css';

function validateNewPassword(password: string): string | null {
  if (password.length < 8) {
    return 'New password must be at least 8 characters.';
  }
  if (!/[A-Z]/.test(password)) {
    return 'New password must include at least one uppercase letter.';
  }
  if (!/[\d\W]/.test(password)) {
    return 'New password must include at least one number or special character.';
  }
  return null;
}

export default function ForcePasswordPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshSession } = useAuth();
  const force = searchParams.get('force') === 'true';

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
    submit?: string;
  }>({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!user?.mustChangePassword && !force) {
      navigate(user?.role === 'admin' ? '/admin/dashboard' : '/technician/dashboard', {
        replace: true
      });
    }
  }, [force, navigate, user]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: typeof errors = {};
    const newPasswordError = validateNewPassword(passwordForm.newPassword);
    if (newPasswordError) nextErrors.newPassword = newPasswordError;
    if (passwordForm.confirmPassword !== passwordForm.newPassword) {
      nextErrors.confirmPassword = 'Confirm password must match new password.';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    setErrors({});
    try {
      await apiRequest('/api/auth/change-password', {
        method: 'POST',
        json: passwordForm
      });
      setSuccess(true);
      await refreshSession();
      window.setTimeout(() => {
        navigate(user?.role === 'admin' ? '/admin/dashboard' : '/technician/dashboard', {
          replace: true
        });
      }, 800);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to update password.';
      if (message.toLowerCase().includes('current password')) {
        setErrors({ submit: 'Current password is incorrect' });
      } else {
        setErrors({ submit: message.replace(/^HTTP \d+[^:]+:\s*/, '') });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="quote-page settings-page">
        <div className="quote-container settings-container">
          <header className="quote-header" style={{ marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: '2.1rem', marginBottom: 8 }}>Change password</h1>
              <p style={{ color: 'var(--dash-muted)', margin: 0 }}>
                Set a new password before continuing.
              </p>
            </div>
          </header>

          {force ? (
            <div className="settings-force-banner">
              <p>Please change your temporary password before continuing.</p>
            </div>
          ) : null}

          <section className="quote-card settings-section-card settings-password-card is-highlighted">
            <div className="quote-section-title">Password</div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="force-current-password">
                  Current password
                </label>
                <input
                  id="force-current-password"
                  className="form-input"
                  type="password"
                  autoComplete="current-password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="force-new-password">
                  New password
                </label>
                <input
                  id="force-new-password"
                  className="form-input"
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
                  }
                />
                {errors.newPassword ? (
                  <p className="settings-field-error">{errors.newPassword}</p>
                ) : (
                  <p className="settings-field-helper">
                    Minimum 8 characters, one uppercase letter, and one number or special character.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="force-confirm-password">
                  Confirm new password
                </label>
                <input
                  id="force-confirm-password"
                  className="form-input"
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                  }
                />
                {errors.confirmPassword ? (
                  <p className="settings-field-error">{errors.confirmPassword}</p>
                ) : null}
              </div>
              {errors.submit ? <p className="settings-field-error">{errors.submit}</p> : null}
              {success ? <p className="settings-field-success">Password updated.</p> : null}
              <button type="submit" className="btn-primary settings-inline-action" disabled={saving}>
                {saving ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </section>
        </div>
      </div>
    </AppShell>
  );
}
